// 筋トレホームの「最近のセッション」
//
// セッションのある日を 10 日分ずつ取り、一覧の下端が近づいたら次の 10 日分を読み足す。
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { ja } from 'date-fns/locale'
import { Loader2, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'

import { CLIENT_ID } from '@/api/client'
import { deleteSession, getSessionHistory } from '@/api/services/workout'
import type { WorkoutSessionListItem } from '@/api/types/workout'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'
import { useSyncEventListener } from '@/hooks/useSyncEventListener'
import { parseUtcDate } from '@/lib/date-utils'
import { findScrollParent } from '@/lib/scroll-parent'
import { useAppStore } from '@/store/useAppStore'

/** 1 回に読み込む「セッションのある日」の数 */
const HISTORY_DAYS = 10
/** 下端からこの距離まで来たら次を読みに行く */
const LOAD_MORE_MARGIN_PX = 400

const sessionHistoryKey = ['workout', 'sessions', 'history'] as const

// 同じ日に複数セッションを行った場合、履歴は日付単位でまとめて表示する
type SessionGroup = {
  dateKey: string
  date: Date
  sessions: WorkoutSessionListItem[]
  setCount: number
  totalVolumeKg: number
}

const groupSessionsByDate = (list: WorkoutSessionListItem[]): SessionGroup[] => {
  const map = new Map<string, SessionGroup>()
  for (const s of list) {
    const date = parseUtcDate(s.started_at)
    const dateKey = format(date, 'yyyy-MM-dd')
    const group = map.get(dateKey)
    if (!group) {
      map.set(dateKey, {
        dateKey,
        date,
        sessions: [s],
        setCount: s.set_count,
        totalVolumeKg: s.total_volume_kg,
      })
      continue
    }
    group.sessions.push(s)
    group.setCount += s.set_count
    group.totalVolumeKg += s.total_volume_kg
  }
  const groups = [...map.values()]
  for (const g of groups) {
    g.sessions.sort((a, b) => a.started_at.localeCompare(b.started_at))
  }
  return groups.sort((a, b) => b.dateKey.localeCompare(a.dateKey))
}

type Props = {
  onDeleted: (sessionId: number) => void
}

export const SessionHistory = ({ onDeleted }: Props) => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const startLoading = useAppStore((s) => s.startLoading)
  const stopLoading = useAppStore((s) => s.stopLoading)
  const sentinelRef = useRef<HTMLDivElement | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<WorkoutSessionListItem | null>(null)

  const { data, isPending, isError, hasNextPage, isFetchingNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: sessionHistoryKey,
    queryFn: ({ pageParam, signal }) => getSessionHistory(pageParam, HISTORY_DAYS, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.next_before,
    // セッション画面で記録して戻ってきたときに、最新を取り直す
    staleTime: 0,
  })

  const sessions = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data])
  // ページの境目で同じ日が分かれても、つなげてからまとめるので 1 つになる
  const sessionGroups = useMemo(() => groupSessionsByDate(sessions), [sessions])

  const invalidate = useCallback(() => queryClient.invalidateQueries({ queryKey: sessionHistoryKey }), [queryClient])

  useSyncEventListener(
    useCallback(
      (event) => {
        if (event.type !== 'workout.session.changed') return
        if (event.origin === CLIENT_ID) return
        void invalidate()
      },
      [invalidate],
    ),
  )

  const loadMore = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) return
    void fetchNextPage()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return
    // 置かれたスクロール領域（スワイプタブのパネルなど）を基準に、下端が近づいたかを見る
    const root = findScrollParent(sentinel)

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMore()
      },
      { root, rootMargin: `${LOAD_MORE_MARGIN_PX}px` },
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [loadMore, sessions.length])

  const handleDeleteSession = useCallback(async () => {
    if (!deleteTarget) return
    startLoading()
    try {
      await deleteSession(deleteTarget.id)
      onDeleted(deleteTarget.id)
      await invalidate()
    } catch (err) {
      console.error(err)
      toast.error('削除に失敗しました')
    } finally {
      setDeleteTarget(null)
      stopLoading()
    }
  }, [deleteTarget, invalidate, onDeleted, startLoading, stopLoading])

  useEffect(() => {
    if (isError) toast.error('セッション履歴の取得に失敗しました')
  }, [isError])

  return (
    <div className="space-y-2">
      <h2 className="text-lg font-semibold">最近のセッション</h2>
      {isPending ? (
        <Spinner size={24} />
      ) : sessions.length === 0 ? (
        <div className="text-sm text-muted-foreground py-4">まだセッション履歴がありません</div>
      ) : (
        <div className="grid gap-2">
          {sessionGroups.map((g) => (
            <Card key={g.dateKey} className="p-3 gap-2">
              <div className="flex items-baseline justify-between gap-2">
                <div className="font-medium">{format(g.date, 'M月d日 (E)', { locale: ja })}</div>
                <div className="text-xs text-muted-foreground">
                  {g.sessions.length > 1 && `${g.sessions.length} セッション ・ `}
                  {g.setCount} セット ・ {Math.round(g.totalVolumeKg).toLocaleString()} kg
                </div>
              </div>
              <div className="divide-y divide-border/50">
                {g.sessions.map((s) => (
                  <div
                    key={s.id}
                    className="flex justify-between items-start gap-2 py-2 -mx-1 px-1 rounded cursor-pointer hover:bg-accent"
                    onClick={() => navigate(`/workout/sessions/${s.id}`)}
                  >
                    <div>
                      <div className="text-sm">{s.name ?? s.template_name ?? 'セッション'}</div>
                      <div className="text-xs text-muted-foreground">
                        {format(parseUtcDate(s.started_at), 'HH:mm', { locale: ja })}
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="text-right text-xs">
                        <div>{s.set_count} セット</div>
                        <div className="text-muted-foreground">{s.total_volume_kg.toLocaleString()} kg</div>
                        <div
                          className={
                            s.status === 'completed'
                              ? 'text-green-500'
                              : s.status === 'in_progress'
                                ? 'text-orange-500'
                                : 'text-muted-foreground'
                          }
                        >
                          {s.status === 'completed' ? '完了' : s.status === 'in_progress' ? '進行中' : '中断'}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7 text-muted-foreground hover:text-destructive"
                        onClick={(e) => {
                          e.stopPropagation()
                          setDeleteTarget(s)
                        }}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}

      <div ref={sentinelRef} className="h-px" />
      {isFetchingNextPage && (
        <div className="flex justify-center py-3 text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
        </div>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>セッションを削除</AlertDialogTitle>
            <AlertDialogDescription>
              「{deleteTarget?.name ?? deleteTarget?.template_name ?? 'セッション'}」を削除しますか？
              この操作は取り消せません。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>キャンセル</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteSession} className="bg-destructive text-destructive-foreground">
              削除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
